/**
 * SaleActions — ventes du cabinet (Batch #14.279) : les actions de l'ancien module Factures sur chaque ligne.
 * Aperçu / impression PDF · Déposer / retirer du portail client · Envoyer au client (courriel PDF + lien de paiement) ·
 * Envoyer par texto (lien PDF public + lien de paiement, texte validé — Batch #14.336) · Classer dans Google Drive.
 */
import { useState } from "react";
import { Eye, EyeOff, Loader2, Mail, Smartphone, Upload } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import DriveSendButton from "@/components/admin/DriveSendButton";
import SmsSendModal from "@/components/admin/SmsSendModal";
import { errMsg, useGlBase } from "../comptabilite/_shared";

const btn = "text-slate-400 p-1 disabled:opacity-40";

export function openPdf(blob, print = false) {
  const url = URL.createObjectURL(new window.Blob([blob], { type: "application/pdf" }));
  // « noopener » dans window.open renvoie toujours null (faux « Fenêtre bloquée ») : on coupe opener à la main
  const w = window.open(url, "_blank");
  if (!w) toast.error("Fenêtre bloquée — autorisez les fenêtres surgissantes pour voir le PDF");
  else { try { w.opener = null; } catch (_) { /* ignore */ } if (print) w.addEventListener("load", () => setTimeout(() => w.print(), 300)); }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function SaleActions({ d, onDone, compact = true }) {
  const base = useGlBase();
  const [busy, setBusy] = useState("");
  const [sms, setSms] = useState(false);
  const posted = d.post_status !== "draft";
  const deposited = !!d.portal?.deposited;

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const preview = () => run("pdf", async () => { const r = await api.get(`${base}/sales/${d.id}/pdf`, { responseType: "blob" }); openPdf(r.data); });
  const deposit = () => run("deposit", () => api.put(`${base}/sales/${d.id}/${deposited ? "undeposit" : "deposit"}`), deposited ? "Facture retirée du portail client" : "Facture déposée dans le portail client");
  const send = () => {
    if (!window.confirm(`Envoyer la facture ${d.number || ""} au client par courriel (PDF joint${d.balance > 0.005 ? " + lien de paiement" : ""}) ?`)) return;
    run("send", async () => {
      const r = await api.post(`${base}/sales/${d.id}/send`, { origin_url: window.location.origin });
      if (r.data.email_sent) toast.success(`Facture envoyée à ${r.data.recipient}${r.data.recipient_conjoint ? ` et ${r.data.recipient_conjoint}` : ""}`);
      else toast.error(r.data.error || "Courriel non envoyé");
    });
  };
  const Ic = (k, I) => (busy === k ? <Loader2 className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} animate-spin`} /> : <I className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />);

  return (
    <>
      <button onClick={preview} disabled={!!busy} className={`${btn} hover:text-blue-700`} title="Aperçu / imprimer la facture (PDF)" data-testid={`sale-pdf-${d.id}`}>{Ic("pdf", Eye)}</button>
      {posted && (
        <button onClick={deposit} disabled={!!busy} className={`${btn} ${deposited ? "text-indigo-600 hover:text-slate-700" : "hover:text-indigo-700"}`}
          title={deposited ? "Retirer du portail client" : "Déposer dans le portail client (le client voit la facture et peut payer par Stripe)"} data-testid={`sale-deposit-${d.id}`}>
          {Ic("deposit", deposited ? EyeOff : Upload)}
        </button>
      )}
      {posted && <button onClick={send} disabled={!!busy} className={`${btn} hover:text-emerald-700`} title={`Envoyer au client par courriel (PDF + lien de paiement)${d.portal?.sent_at ? ` — déjà envoyée le ${String(d.portal.sent_at).slice(0, 10)}` : ""}`} data-testid={`sale-send-${d.id}`}>{Ic("send", Mail)}</button>}
      {posted && (
        <button onClick={() => setSms(true)} disabled={!!busy} className={`${btn} ${d.portal?.sms_sent_at ? "text-orange-500 hover:text-orange-700" : "hover:text-orange-600"}`}
          title={`Envoyer par texto (lien de la facture + lien de paiement — texte à valider)${d.portal?.sms_sent_at ? ` — déjà envoyé le ${String(d.portal.sms_sent_at).slice(0, 10)}` : ""}`} data-testid={`sale-sms-${d.id}`}>
          {Ic("sms", Smartphone)}
        </button>
      )}
      {posted && <span className="inline-flex [&>button]:px-1.5 [&>button]:py-1 [&>button]:border-0 [&>button]:text-slate-400 [&>button:hover]:bg-transparent" data-testid={`sale-drive-${d.id}`}><DriveSendButton docType="sale" docId={d.id} defaultExpertise="fiscal" title="Classer la facture dans Google Drive" /></span>}
      {sms && <SmsSendModal kind="sale" id={d.id} title={`Facture ${d.number || ""}`} onClose={() => setSms(false)} onSent={() => onDone?.()} />}
    </>
  );
}
